import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { getMunicipios, cityKey } from '../../services/ibgeService'

/**
 * Searchable municipality picker bound to a UF. Emits a synthetic
 * { target: { name, value } } so it plugs into useForm.handleChange like any
 * other input. Value is the canonical IBGE name (what we persist).
 */
const CitySelect = ({ id, name, value, uf, onChange, placeholder, invalid, disabled }) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!uf) { setOptions([]); return undefined }
    setLoading(true)
    setFailed(false)
    getMunicipios(uf)
      .then((list) => { if (!cancelled) setOptions(list.map((m) => ({ value: m.nome, label: m.nome }))) })
      .catch(() => { if (!cancelled) setFailed(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [uf])

  // Match the stored value even if it is a legacy variant ("GUAPIACU").
  const selected = useMemo(() => {
    if (!value) return null
    const k = cityKey(value)
    return options.find((o) => cityKey(o.value) === k) || { value, label: value }
  }, [value, options])

  const emit = (v) => onChange({ target: { name, value: v } })

  if (failed) {
    // IBGE unreachable: degrade to free text rather than block the form.
    return (
      <input
        id={id} name={name} type="text" className={`form-control${invalid ? ' is-invalid' : ''}`}
        value={value || ''} onChange={(e) => emit(e.target.value)} placeholder={placeholder} disabled={disabled}
      />
    )
  }

  return (
    <Select
      inputId={id}
      name={name}
      classNamePrefix="select2-selection"
      className={invalid ? 'is-invalid' : ''}
      options={options}
      value={selected}
      onChange={(opt) => emit(opt ? opt.value : '')}
      isLoading={loading}
      isDisabled={disabled || !uf}
      isClearable
      isSearchable
      placeholder={!uf ? 'Selecione o estado primeiro' : placeholder || 'Busque a cidade'}
      noOptionsMessage={({ inputValue }) => (inputValue ? 'Nenhuma cidade encontrada' : 'Digite para buscar')}
      loadingMessage={() => 'Carregando cidades...'}
      // Accent-insensitive search: "guapiacu" finds "Guapiaçu".
      filterOption={(opt, input) => cityKey(opt.label).includes(cityKey(input))}
    />
  )
}

CitySelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  uf: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default CitySelect
